import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const STORAGE_KEYS = {
  token: 'authToken',
  refresh: 'adminRefreshToken',
  role: 'userRole',
  roles: 'adminRoleKeys',
  perms: 'adminPermissions',
  userId: 'userId',
  driverId: 'driverId',
  mfaEnabled: 'adminMfaEnabled',
};

function readJson(key) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [driverId, setDriverId] = useState(null);
  const [adminRoles, setAdminRoles] = useState([]);
  const [adminPermissions, setAdminPermissions] = useState([]);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEYS.token);
    const role = localStorage.getItem(STORAGE_KEYS.role);
    const storedDriverId = localStorage.getItem(STORAGE_KEYS.driverId);
    const storedUserId = localStorage.getItem(STORAGE_KEYS.userId);
    if (token && role) {
      setIsAuthenticated(true);
      setUserRole(role);
      if (storedUserId) setUserId(storedUserId);
      if (role === 'DRIVER' && storedDriverId) setDriverId(storedDriverId);
      setAdminRoles(readJson(STORAGE_KEYS.roles) || []);
      setAdminPermissions(readJson(STORAGE_KEYS.perms) || []);
      setMfaEnabled(localStorage.getItem(STORAGE_KEYS.mfaEnabled) === 'true');
    }
  }, []);

  const loginUser = async (username, password) => {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!response.ok) throw new Error('Invalid credentials');
    const data = await response.json();
    localStorage.setItem(STORAGE_KEYS.token, data.token);
    localStorage.setItem(STORAGE_KEYS.role, data.role);
    if (data.userId) {
      localStorage.setItem(STORAGE_KEYS.userId, data.userId);
      setUserId(String(data.userId));
    }
    if (data.role === 'DRIVER' && data.driverId) {
      localStorage.setItem(STORAGE_KEYS.driverId, data.driverId);
      setDriverId(data.driverId);
    }
    setIsAuthenticated(true);
    setUserRole(data.role);
    return data;
  };

  const registerUser = async (userData) => {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });
    if (!response.ok) throw new Error('Registration failed');
    return response.json();
  };

  const loginWithToken = (token, role, userIdVal, driverIdVal) => {
    localStorage.setItem(STORAGE_KEYS.token, token);
    localStorage.setItem(STORAGE_KEYS.role, role);
    if (userIdVal) {
      localStorage.setItem(STORAGE_KEYS.userId, userIdVal);
      setUserId(String(userIdVal));
    }
    if (driverIdVal) {
      localStorage.setItem(STORAGE_KEYS.driverId, driverIdVal);
      setDriverId(driverIdVal);
    }
    setIsAuthenticated(true);
    setUserRole(role);
  };

  /** Admin session payload from /api/v1/auth/admin/firebase-login or /mfa-verify. */
  const loginAdmin = useCallback((payload) => {
    const {
      accessToken, refreshToken, userId: uid,
      roles = [], permissions = [], mfaEnabled: mfa = false,
    } = payload;
    localStorage.setItem(STORAGE_KEYS.token, accessToken);
    if (refreshToken) localStorage.setItem(STORAGE_KEYS.refresh, refreshToken);
    localStorage.setItem(STORAGE_KEYS.role, 'ADMIN');
    localStorage.setItem(STORAGE_KEYS.userId, String(uid));
    localStorage.setItem(STORAGE_KEYS.roles, JSON.stringify(roles));
    localStorage.setItem(STORAGE_KEYS.perms, JSON.stringify(permissions));
    localStorage.setItem(STORAGE_KEYS.mfaEnabled, mfa ? 'true' : 'false');

    setIsAuthenticated(true);
    setUserRole('ADMIN');
    setUserId(String(uid));
    setAdminRoles(roles);
    setAdminPermissions(permissions);
    setMfaEnabled(mfa);
  }, []);

  const refreshAdminSession = useCallback(async () => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.refresh);
    if (!refreshToken) return false;
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/auth/admin/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    loginAdmin(data);
    return true;
  }, [loginAdmin]);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.refresh);
    if (refreshToken) {
      try {
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/auth/admin/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
      } catch {
        // Best-effort; clear local state regardless.
      }
    }
    Object.values(STORAGE_KEYS).forEach((k) => localStorage.removeItem(k));
    setIsAuthenticated(false);
    setUserRole(null);
    setDriverId(null);
    setUserId(null);
    setAdminRoles([]);
    setAdminPermissions([]);
    setMfaEnabled(false);
  }, []);

  const hasPermission = useCallback(
    (perm) => !perm || adminPermissions.includes(perm),
    [adminPermissions]
  );

  const hasAnyPermission = useCallback(
    (perms) => !perms || perms.length === 0 || perms.some((p) => adminPermissions.includes(p)),
    [adminPermissions]
  );

  const hasRole = useCallback(
    (role) => !role || adminRoles.includes(role),
    [adminRoles]
  );

  return (
    <AuthContext.Provider value={{
      isAuthenticated, userRole, driverId, userId,
      adminRoles, adminPermissions, mfaEnabled,
      loginUser, loginWithToken, loginAdmin, refreshAdminSession,
      registerUser, logout,
      hasPermission, hasAnyPermission, hasRole,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
